// Push to this device (PLAN-2026-09-26-bell B3, K3): what this browser can
// do, and turning it on and off.
//
// THE PROMPT FOLLOWS A TAP, NEVER A PAGE LOAD (INSTALL.md §3): `turnOn` is
// the only place the permission is asked for, and a screen calls it from a
// button. A browser that was asked once and said no cannot be asked again by
// a site; the screen says where the switch is instead.
//
// ON AN IPHONE OR IPAD, PUSH EXISTS ONLY IN THE INSTALLED APP (INSTALL.md §3),
// and installing means signing in once more inside it (§1). So in a Safari
// tab the state is `needs-install`, and the screen says both things before
// anybody commits to them (CLAUDE.md, "Say what will happen").
//
// THE SERVER KNOWS A DEVICE BY ITS ENDPOINT: posting the same subscription
// again answers with the row it already has, which is how *Turn off* finds
// the id to delete without this device having to remember it.
//
// A SUBSCRIPTION IS THE BROWSER'S, NOT THE SESSION'S (the review of B4). So
// this device remembers whose it turned on, and a subscription that is not
// the signed-in person's — they signed out, their session lapsed, their
// account went — is dropped from the browser (`settleDevice`); the push
// service then answers `gone` to the server, which drops the row. Without it
// the next person would read "on" while the last one's trade offers arrived.
//
// THE SERVER'S ROW ENDS WITH THE SESSION IT WAS MADE UNDER (the audit of
// 2026-10-20): revoking a device, signing the others out and a reset drop
// it there. So a device still signed in sends its subscription again once
// each time the app opens (`settleDevice`), and is put back under the session
// it holds now.

import { PushSubscriptionRef, VapidKey } from '@fsp/domain';
import { z } from 'zod';
import { get, messageOf, send } from './api';
import { isAppleTouch, isInstalled } from '../install/platform';
import { WORKER_URL } from './serviceWorker';
import { storageKey } from './storage';

export type PushState =
  /** No service worker or no push in this browser. */
  | 'unsupported'
  /** An iPhone or iPad in a browser tab: push needs the installed app. */
  | 'needs-install'
  /** The browser was told no for this site. */
  | 'denied'
  | 'off'
  | 'on';

const WORKER = WORKER_URL;
const OWNER_KEY = storageKey('push.owner.v1');
const SENT_KEY = storageKey('push.sent.v1');

function owner(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}

function setOwner(userId: string | null): void {
  try {
    if (userId === null) localStorage.removeItem(OWNER_KEY);
    else localStorage.setItem(OWNER_KEY, userId);
  } catch {
    // Storage blocked: the subscription is dropped at the next settle, which
    // is the safe way for this to fail.
  }
}
function sentThisVisit(userId: string): boolean {
  try {
    return sessionStorage.getItem(SENT_KEY) === userId;
  } catch {
    // Storage blocked: say it was sent, rather than send on every settle.
    return true;
  }
}

function markSent(userId: string): void {
  try {
    sessionStorage.setItem(SENT_KEY, userId);
  } catch {
    // Storage blocked: nothing was promised.
  }
}
const Nothing = z.unknown();

// One reading of the device for push and for installing (install/platform.ts).
const appleTouch = (): boolean => isAppleTouch(navigator.userAgent, navigator.maxTouchPoints);

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration(WORKER);
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Where this device stands for this person. Asks nothing of them. */
export async function pushState(userId: string): Promise<PushState> {
  if (!supported()) return appleTouch() && !isInstalled() ? 'needs-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') return 'off';
  return (await currentSubscription()) !== null && owner() === userId ? 'on' : 'off';
}

/**
 * Drop a subscription that is not the signed-in person's (`userId` null for
 * nobody). Called once the session is known, and whenever it changes.
 */
export async function settleDevice(userId: string | null): Promise<void> {
  try {
    if (!supported()) return;
    const sub = await currentSubscription();
    if (sub === null) {
      setOwner(null);
      return;
    }
    if (owner() === userId) {
      // ONCE EACH TIME THE APP OPENS, THIS DEVICE TELLS THE SERVER AGAIN (the
      // audit of 2026-10-20). The server drops a subscription when the
      // session it was made under ends, and every one on a password reset;
      // this device, still signed in, puts its own back under the session it
      // holds now. Once a tab's life, kept in session storage, so the layer
      // reads no clock.
      if (userId !== null && !sentThisVisit(userId)) {
        const sent = await send('POST', '/me/push', bodyOf(sub), PushSubscriptionRef);
        if (sent.status === 'ok') markSent(userId);
      }
      return;
    }
    await sub.unsubscribe();
    setOwner(null);
  } catch {
    // Nothing to do here that a later settle will not do.
  }
}

/** "Chrome on Windows": a name the person would recognize in a list of devices. */
export function deviceLabel(ua: string = navigator.userAgent): string {
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'A browser';
  const system = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Mac OS X|Macintosh/.test(ua)
            ? 'Mac'
            : /Linux/.test(ua)
              ? 'Linux'
              : null;
  return system === null ? browser : `${browser} on ${system}`;
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** The subscription as the server takes it. */
function bodyOf(sub: PushSubscription) {
  const json = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    device: deviceLabel(),
    declarative: false,
  };
}

export type PushOutcome = { ok: true; state: PushState } | { ok: false; message: string };

/**
 * Ask, subscribe, tell the server. Called from a tap. A "no" at the prompt
 * is an answer, not a failure: the state that comes back says so.
 */
export async function turnOn(userId: string): Promise<PushOutcome> {
  if (!supported()) return { ok: true, state: await pushState(userId) };
  const answer = await Notification.requestPermission();
  if (answer !== 'granted') return { ok: true, state: await pushState(userId) };
  const key = await get('/push/vapid', VapidKey);
  if (key.status !== 'ok')
    return {
      ok: false,
      message: messageOf(key, 'Notifications to devices are not set up on this site yet.'),
    };
  try {
    const reg = await navigator.serviceWorker.register(WORKER);
    await navigator.serviceWorker.ready;
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(key.data.publicKey),
      }));
    const saved = await send('POST', '/me/push', bodyOf(sub), PushSubscriptionRef);
    if (saved.status !== 'ok') {
      await sub.unsubscribe().catch(() => undefined);
      return { ok: false, message: messageOf(saved, 'This device could not be added.') };
    }
    setOwner(userId);
    return { ok: true, state: 'on' };
  } catch (e) {
    return {
      ok: false,
      message: `This browser would not turn them on (${e instanceof Error ? e.message : String(e)}).`,
    };
  }
}

/**
 * Stop pushing to this device: the server forgets it first, then the browser.
 * In that order, so a failure leaves a device that is still told rather than
 * a row the server keeps pushing to a subscription nobody holds.
 */
export async function turnOff(userId: string): Promise<PushOutcome> {
  if (!supported()) return { ok: true, state: await pushState(userId) };
  const sub = await currentSubscription();
  if (sub === null) return { ok: true, state: await pushState(userId) };
  const found = await send('POST', '/me/push', bodyOf(sub), PushSubscriptionRef);
  if (found.status !== 'ok')
    return { ok: false, message: messageOf(found, 'This device could not be turned off.') };
  const gone = await send(
    'DELETE',
    `/me/push/${encodeURIComponent(found.data.id)}`,
    undefined,
    Nothing,
  );
  if (gone.status !== 'ok' && !(gone.status === 'refused' && gone.code === 'not_found'))
    return { ok: false, message: messageOf(gone, 'This device could not be turned off.') };
  await sub.unsubscribe().catch(() => undefined);
  setOwner(null);
  return { ok: true, state: await pushState(userId) };
}

/** How long a sign-out gives the server to forget this device. */
const FORGET_MS = 3_000;

/**
 * On sign-out: this device stops being told about the account that is
 * leaving it (B-191). The server is asked to forget it for up to three
 * seconds; the browser's subscription is dropped either way, so a server
 * that did not answer hears `gone` from the push service at the next send.
 */
export async function forgetThisDevice(userId: string): Promise<void> {
  try {
    if (!supported() || Notification.permission !== 'granted') return;
    await Promise.race([
      turnOff(userId).catch(() => undefined),
      new Promise((done) => setTimeout(done, FORGET_MS)),
    ]);
    const sub = await currentSubscription();
    await sub?.unsubscribe();
    setOwner(null);
  } catch {
    // The push service answers `gone` for a dead subscription, and the pump
    // drops the row then.
  }
}
